import { useState, useMemo, useEffect } from 'react';
import type { MaterialFilters } from '../../../features/materials/hooks';
import { useMaterialsPanelStore, type MaterialsPanelMode } from '../../../stores/materialsPanelStore';
import { brandConfig } from '../../../config/brands';
import type { Tag } from '../../../features/tags/types';
import {
  MATERIALS_FILTER_CARRYOVER_KEY,
  type MaterialsFilterSnapshot,
  type MaterialsTab,
  type TabType,
} from './types';

interface UseMaterialsFiltersParams {
  mode: MaterialsPanelMode;
  selectedAsin?: string;
  selectedBrandId?: string;
  hideAmazonComplianceFilter: boolean;
  initialFileTypes?: string[];
  /** 種別フィルタを UI に出していない (= initialFileTypes で固定している) か。true のとき「フィルタをリセット」でも
   *  種別は initialFileTypes に戻し、アクティブ判定にも数えない (音声ピッカー等で種別が外れないように) */
  fileTypesLocked?: boolean;
  initialAspectRatios?: string[];
  /** 取得元フィルタの初期値 (モーダル埋め込み時。例: BGM ピッカーで ['bgm'])。リセットでは空に戻る */
  initialSourceTypes?: string[];
  /** ナレーションの生成元シナリオ絞り込みの初期値 (ナレーション追加ピッカーで開いているシナリオ)。リセットでは外れる */
  initialNarrationScenarioId?: string;
  /** 最大尺フィルタを表に出しているか (3点リーダーのアクティブ表示判定に使用) */
  showMaxDurationFilter: boolean;
  /** 組織のタグ一覧 (tagsQuery.data)。URL/強制タグフィルタの ID→名前変換に使用 */
  tags: Tag[] | undefined;
  activeTab: TabType;
  /** 強制フィルタによるタブ切り替え (refetch を伴わない生のタブ変更) */
  switchTab: (tab: MaterialsTab) => void;
}

/**
 * SimpleMaterialsList のフィルタ状態を集約したフック。
 *
 * 検索/種別/アスペクト比/取得元/商品/ブランド/タグ/制作進捗/最大尺/Amazon適合の
 * 各フィルタ状態と、URL パラメータ・sessionStorage 引き継ぎ・ストア強制値の反映、
 * サーバーサイドフィルタ (serverFilters) の構築を担う。
 */
export function useMaterialsFilters({
  mode,
  selectedAsin,
  selectedBrandId,
  hideAmazonComplianceFilter,
  initialFileTypes,
  fileTypesLocked = false,
  initialAspectRatios,
  initialSourceTypes,
  initialNarrationScenarioId,
  showMaxDurationFilter,
  tags,
  activeTab,
  switchTab,
}: UseMaterialsFiltersParams) {
  const [searchQuery, setSearchQuery] = useState('');
  // 検索・最大尺など非推奨フィルタをインラインで展開する 3 点リーダーの開閉
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [fileTypeFilter, setFileTypeFilter] = useState<string[]>(initialFileTypes ?? []);
  const [aspectRatioFilter, setAspectRatioFilter] = useState<string[]>(initialAspectRatios ?? []);
  const [sourceTypeFilter, setSourceTypeFilter] = useState<string[]>(initialSourceTypes ?? []);
  // ナレーションの生成元シナリオ ('' = 指定なし)。サーバー側で絞り込む (narration_scenario_id)
  const [narrationScenarioFilter, setNarrationScenarioFilter] = useState(initialNarrationScenarioId ?? '');
  const forcedAspectRatios = useMaterialsPanelStore((state) => state.forcedAspectRatios);
  const forcedFileTypes = useMaterialsPanelStore((state) => state.forcedFileTypes);
  const forcedTagFilter = useMaterialsPanelStore((state) => state.forcedTagFilter);
  const clearForcedTagFilter = useMaterialsPanelStore((state) => state.clearForcedTagFilter);
  const forcedProductFilter = useMaterialsPanelStore((state) => state.forcedProductFilter);
  const clearForcedProductFilter = useMaterialsPanelStore((state) => state.clearForcedProductFilter);

  // URLのtagパラメータから初期タグフィルタを設定（トップページからのプロジェクト遷移用）
  const [initialTagId] = useState(() => new URLSearchParams(window.location.search).get('tag'));
  // URLのproductパラメータ（`<type>:<id>`）から初期プロダクトフィルタを設定
  const [initialProductFilter] = useState(() => new URLSearchParams(window.location.search).get('product'));
  // URL の brand_id パラメータから初期ブランドフィルタを設定
  const [initialBrandId] = useState(() => new URLSearchParams(window.location.search).get('brand_id'));
  const [filterExpanded, setFilterExpanded] = useState(
    mode === 'select' || !!initialTagId || !!initialProductFilter || !!initialBrandId
  );
  const [productFilter, setProductFilter] = useState(initialProductFilter ?? '');
  const [brandFilter, setBrandFilter] = useState(initialBrandId ?? '');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [workflowStageFilter, setWorkflowStageFilter] = useState<string[]>([]);
  // 最大尺(秒)フィルタ。null = 指定なし
  const [maxDurationFilter, setMaxDurationFilter] = useState<number | null>(null);
  // Amazon商品画像適合フィルタ（selectモード かつ Amazon 連携有効パートナー、かつ
  // Amazon ASIN が差し替え対象のときのみデフォルトでtrue）。
  // マニュアル登録商品の差し替え (selectedAsin なし) では推奨規定で絞り込まない。
  const [amazonProductImageFilter, setAmazonProductImageFilter] = useState(
    mode === 'select' && brandConfig.features.amazonIntegration && !!selectedAsin && !hideAmazonComplianceFilter
  );

  // 外部から強制されたアスペクト比フィルタを反映。store の強制値が無い場合は、
  // モーダル埋め込み時の初期値 (initialAspectRatios) があればそれを既定にする。
  useEffect(() => {
    if (forcedAspectRatios) {
      setAspectRatioFilter(forcedAspectRatios);
      setFilterExpanded(true);
    } else {
      setAspectRatioFilter(initialAspectRatios ?? []);
    }
  }, [forcedAspectRatios, initialAspectRatios]);

  // 外部から強制された種別フィルタを反映。同上で initialFileTypes を既定にする。
  useEffect(() => {
    if (forcedFileTypes) {
      setFileTypeFilter(forcedFileTypes);
      setFilterExpanded(true);
    } else {
      setFileTypeFilter(initialFileTypes ?? []);
    }
  }, [forcedFileTypes, initialFileTypes]);

  // 素材パネルの「素材ページを開く」から引き継いだフィルタを反映（manage モードのみ）。
  // 強制フィルタ用 useEffect（aspect/種別を空に戻す）より後に宣言することで、
  // マウント時の実行順で上書きされないようにしている。
  useEffect(() => {
    if (mode !== 'manage') return;
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(MATERIALS_FILTER_CARRYOVER_KEY);
      if (raw) sessionStorage.removeItem(MATERIALS_FILTER_CARRYOVER_KEY);
    } catch {
      raw = null;
    }
    if (!raw) return;
    let snapshot: Partial<MaterialsFilterSnapshot>;
    try {
      snapshot = JSON.parse(raw);
    } catch {
      return;
    }
    if (snapshot.search) setSearchQuery(snapshot.search);
    if (snapshot.fileTypes?.length) setFileTypeFilter(snapshot.fileTypes);
    if (snapshot.aspectRatios?.length) setAspectRatioFilter(snapshot.aspectRatios);
    if (snapshot.sourceTypes?.length) setSourceTypeFilter(snapshot.sourceTypes);
    if (snapshot.tags?.length) setTagFilter(snapshot.tags);
    if (snapshot.workflowStages?.length) setWorkflowStageFilter(snapshot.workflowStages);
    if (snapshot.product) setProductFilter(snapshot.product);
    if (snapshot.brandId) setBrandFilter(snapshot.brandId);
    const hasAny = !!(snapshot.search || snapshot.fileTypes?.length || snapshot.aspectRatios?.length
      || snapshot.sourceTypes?.length || snapshot.tags?.length || snapshot.workflowStages?.length
      || snapshot.product || snapshot.brandId);
    if (hasAny) setFilterExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URLのtagパラメータ（ID）をタグ名に変換してフィルタに設定
  useEffect(() => {
    if (!initialTagId || !tags) return;
    const tag = tags.find(t => t.id === initialTagId);
    if (tag) {
      setTagFilter([tag.name]);
    }
  }, [initialTagId, tags]);

  // ストアから強制されたタグフィルタ（タグ管理画面からのジャンプ）を反映
  useEffect(() => {
    if (!forcedTagFilter || !tags) return;
    const tag = tags.find(t => t.id === forcedTagFilter.tagId);
    if (!tag) return;
    const targetTab: MaterialsTab = forcedTagFilter.folder === 'shared' ? 'cloud' : 'temporary';
    if (activeTab !== targetTab) {
      switchTab(targetTab);
    }
    setTagFilter([tag.name]);
    setFilterExpanded(true);
    clearForcedTagFilter();
  }, [forcedTagFilter, tags, activeTab, switchTab, clearForcedTagFilter]);

  // ストアから強制されたプロダクトフィルタ（HypeApp トップ等からのジャンプ）を反映
  useEffect(() => {
    if (!forcedProductFilter) return;
    const targetTab: MaterialsTab = forcedProductFilter.folder === 'shared' ? 'cloud' : 'temporary';
    if (activeTab !== targetTab) {
      switchTab(targetTab);
    }
    setProductFilter(forcedProductFilter.productFilter);
    setFilterExpanded(true);
    clearForcedProductFilter();
  }, [forcedProductFilter, activeTab, switchTab, clearForcedProductFilter]);

  // selectモード時、selectedBrandIdが変更されたらbrandFilterに設定
  useEffect(() => {
    if (mode === 'select' && selectedBrandId) {
      setBrandFilter(selectedBrandId);
    }
  }, [mode, selectedBrandId]);

  // 検索入力のデバウンス（300ms）
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // タグ名からタグIDのリストを取得
  const tagIdsForFilter = useMemo(() => {
    if (tagFilter.length === 0 || !tags) return undefined;
    const ids = tagFilter
      .map(name => tags.find(t => t.name === name)?.id)
      .filter((id): id is string => !!id);
    return ids.length > 0 ? ids : undefined;
  }, [tagFilter, tags]);

  // サーバーサイドフィルタ用のパラメータを構築
  const serverFilters = useMemo<MaterialFilters>(() => {
    const filters: MaterialFilters = {};

    // ファイル種別
    if (fileTypeFilter.length > 0) {
      filters.file_types = fileTypeFilter;
    }

    // 取得元（複合値を実際の source_type に展開）
    if (sourceTypeFilter.length > 0) {
      const noSource = sourceTypeFilter.includes('__no_source__');
      const sourceTypes = sourceTypeFilter
        .filter(s => s !== '__no_source__')
        .flatMap(s => s.split(','));
      if (sourceTypes.length > 0) {
        filters.source_types = sourceTypes;
      }
      if (noSource) {
        filters.no_source = true;
      }
    }

    // 商品フィルタ
    if (productFilter) {
      if (productFilter === '__no_product__') {
        filters.no_product = true;
      } else {
        const [type, id] = productFilter.split(':') as ['amazon' | 'manual', string];
        filters.product_id = id;
        filters.product_type = type;
      }
    }

    // タグ
    if (tagIdsForFilter) {
      filters.tag_ids = tagIdsForFilter;
    }

    // 検索（デバウンス済み）
    if (debouncedSearch) {
      filters.search = debouncedSearch;
    }

    // アスペクト比
    if (aspectRatioFilter.length > 0) {
      filters.aspect_ratios = aspectRatioFilter;
    }

    // ブランド
    if (brandFilter) {
      if (brandFilter === '__no_brand__') {
        filters.no_brand = true;
      } else {
        filters.brand_id = brandFilter;
      }
    }

    // 制作進捗 (workflow stage)
    if (workflowStageFilter.length > 0) {
      const noStage = workflowStageFilter.includes('__no_workflow_stage__');
      const stageIds = workflowStageFilter.filter((v) => v !== '__no_workflow_stage__');
      if (stageIds.length > 0) {
        filters.workflow_stage_ids = stageIds;
      }
      if (noStage) {
        filters.no_workflow_stage = true;
      }
    }

    // 最大尺(秒)
    if (maxDurationFilter != null) {
      filters.max_duration_seconds = maxDurationFilter;
    }

    // ナレーションの生成元シナリオ
    if (narrationScenarioFilter) {
      filters.narration_scenario_id = narrationScenarioFilter;
    }

    return filters;
  }, [fileTypeFilter, sourceTypeFilter, productFilter, tagIdsForFilter, debouncedSearch, aspectRatioFilter, brandFilter, workflowStageFilter, maxDurationFilter, narrationScenarioFilter]);

  // フィルタのクリア
  const clearFilters = () => {
    setSearchQuery('');
    setFileTypeFilter(fileTypesLocked ? (initialFileTypes ?? []) : []);
    setAspectRatioFilter([]);
    setProductFilter('');
    setBrandFilter('');
    setTagFilter([]);
    setSourceTypeFilter([]);
    setAmazonProductImageFilter(false);
    setWorkflowStageFilter([]);
    setMaxDurationFilter(null);
    setNarrationScenarioFilter('');
  };

  const hasActiveFilters = !!(searchQuery || (!fileTypesLocked && fileTypeFilter.length > 0) || aspectRatioFilter.length > 0 || productFilter || brandFilter || tagFilter.length > 0 || sourceTypeFilter.length > 0 || amazonProductImageFilter || workflowStageFilter.length > 0 || maxDurationFilter != null || !!narrationScenarioFilter);

  // 3 点リーダー (検索 / 最大尺) のアクティブ表示。最大尺を表に出している場合は
  // そのバッジ判定から除外する。
  const overflowHasActive = !!(searchQuery || (!showMaxDurationFilter && maxDurationFilter != null));

  /** 現在のフィルタ状態のスナップショット（素材ページへの引き継ぎ用） */
  const getSnapshot = (): MaterialsFilterSnapshot => ({
    search: searchQuery,
    fileTypes: fileTypeFilter,
    aspectRatios: aspectRatioFilter,
    sourceTypes: sourceTypeFilter,
    tags: tagFilter,
    workflowStages: workflowStageFilter,
    product: productFilter,
    brandId: brandFilter,
  });

  return {
    searchQuery, setSearchQuery,
    overflowMenuOpen, setOverflowMenuOpen,
    fileTypeFilter, setFileTypeFilter,
    aspectRatioFilter, setAspectRatioFilter,
    sourceTypeFilter, setSourceTypeFilter,
    filterExpanded, setFilterExpanded,
    productFilter, setProductFilter,
    brandFilter, setBrandFilter,
    tagFilter, setTagFilter,
    workflowStageFilter, setWorkflowStageFilter,
    maxDurationFilter, setMaxDurationFilter,
    narrationScenarioFilter, setNarrationScenarioFilter,
    amazonProductImageFilter, setAmazonProductImageFilter,
    serverFilters,
    clearFilters,
    hasActiveFilters,
    overflowHasActive,
    getSnapshot,
  };
}

export type MaterialsFiltersApi = ReturnType<typeof useMaterialsFilters>;
